import { useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { GetMessagesSetQuery, MessagesSetState, useUpdateMessagesSetMutation } from 'data/__generated__';

import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';

import { FormValue, MessagesSetFormContent } from './MessagesSetFormContent';

type ParamTypes = {
    messagesSetId: string;
};

export const MessagesSetForm = ({ messagesSet }: { messagesSet: GetMessagesSetQuery['messagesSet'] }) => {
    const { messagesSetId = '' } = useParams<ParamTypes>();

    const [updateMessagesSet, { loading: updateMessagesSetLoading }] = useUpdateMessagesSetMutation();

    const [formValue, setFormValue] = useState<FormValue>({
        name: messagesSet?.name || '',
        messages:
            messagesSet?.messages.map((message) => {
                return { body: message.body, redirectUrl: message.redirectUrl };
            }) || [],
    });

    async function onSubmit() {
        const updateInput = {
            ...formValue,
            messages: formValue.messages.map((message) => {
                return { ...message, redirectUrl: message.redirectUrl || null };
            }),
            state: MessagesSetState.Regular,
        };
        const { data } = await updateMessagesSet({
            variables: {
                messagesSetId,
                updateInput,
            },
        });

        if (data) {
            const {
                updateMessagesSet: { updatedMessagesSet, success, errors },
            } = data;
            if (success && updatedMessagesSet) {
                toast.success('La série a bien été mise à jour !');
            }

            if (errors) {
                errors.forEach((error) => {
                    toast.error(error, { autoClose: false });
                });
                if (errors.length > 1) {
                    toast.info('Cliquez pour fermer toutes les notifications', {
                        autoClose: false,
                        onClick: () => toast.dismiss(),
                    });
                }

                throw Error("Une erreur inconnue s'est produite");
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    }

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/messages-sets' }],
        },
    ];

    return (
        <Form>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.messagesSetDetails} />
                </HeaderTitle>
                <ActionsAndSave
                    actionGroups={actionGroups}
                    onSave={onSubmit}
                    saveLoading={updateMessagesSetLoading}
                    saveTitle="Mettre à jour"
                />
            </Header>
            <Content>
                <ScrollableContent>
                    <MessagesSetFormContent formValue={formValue} setFormValue={setFormValue} />
                </ScrollableContent>
            </Content>
        </Form>
    );
};

const Form = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;
